import { Field, FieldProps } from 'formik';
import { PhoneNumber } from 'libphonenumber-js';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import { IErrorMessage } from '../../components/errorMessage/errorMessage.type';
import { InfoTooltip } from '../../components/InfoTooltip';
import InputField from '../../components/InputField';
import CallingCodeSelectorContainer from './CallingCodeSelectorContainer';
import { enforceFormat, phoneNumberOnChange } from './PhoneNumberFormatting';
import {
    getCountryCode,
    getDefaultValue,
    getPhoneNumber,
    isEmpty,
    MAX_LENGTH,
    validatePhoneNumber,
} from './PhoneNumberValidation';
import { ValidatorRule } from '../../validator/validatorRule.constants';
import { PhoneNumberFieldValidator } from '../../validator/phoneNumberFieldValidator';

interface IPhoneNumberFieldProps {
    autoFocus?: boolean;
    defaultCountry?: string;
    callingCodeFieldId?: string;
    phoneNumberFieldId?: string;
    intl: InjectedIntl;
    tooltipText?: string;
    value?: string;
    required?: boolean;
    validatorRules?: ValidatorRule[];
    label: string;
}

interface IPhoneNumberFieldState {
    currentCallingCode?: string;
    currentCountry?: string;
    initialDisplayValue: string;
    isTouched: boolean;
    maxLength: number;
    phoneNumber?: PhoneNumber;
}

const FlagButtonWrapper = styled.div`
    position: absolute;
    top: -2px;
`;

const PhoneNumberFieldWrapper = styled.div`
    position: relative;
`;

const PhoneNumberFieldInputWrapper = styled.div`
    position: relative;
    input[type='tel'] {
        padding-left: 48px;
        position: relative;
    }
`;

class PhoneNumberField extends React.Component<IPhoneNumberFieldProps, IPhoneNumberFieldState> {
    private readonly ID: string;
    private readonly LABEL_ID: string;

    private phoneNumberFieldValidator: PhoneNumberFieldValidator;

    constructor(props: IPhoneNumberFieldProps) {
        super(props);

        const { phoneNumberFieldId, value, label } = this.props;

        this.ID = phoneNumberFieldId ? phoneNumberFieldId : 'phoneNumber';
        this.LABEL_ID = `label.${label}`;
        const initialDisplayValue = enforceFormat(value || '');

        const phoneNumber = getPhoneNumber(initialDisplayValue);

        const { defaultCountry, validatorRules = [] } = this.props;

        this.state = {
            currentCountry: getCountryCode(defaultCountry, initialDisplayValue, phoneNumber),
            initialDisplayValue,
            isTouched: false,
            maxLength: MAX_LENGTH,
            phoneNumber,
        };

        this.validatePhoneNumber = this.validatePhoneNumber.bind(this);
        this.renderInputField = this.renderInputField.bind(this);

        this.phoneNumberFieldValidator = new PhoneNumberFieldValidator(validatorRules);
    }

    public componentDidUpdate(prevProps: IPhoneNumberFieldProps) {
        const { value, defaultCountry } = this.props;
        const { currentCountry, currentCallingCode } = this.state;
        if (value && value !== prevProps.value) {
            const phoneNumber = getPhoneNumber(value);
            const maxNumericLength = currentCountry === 'US' ? 11 : 16;
            this.setState({
                currentCountry: getCountryCode(
                    defaultCountry,
                    value,
                    phoneNumber,
                    currentCountry,
                    currentCallingCode,
                    true,
                ),
                isTouched: true,
                maxLength: value.replace(/[0-9]/g, '').length + maxNumericLength,
                phoneNumber: getPhoneNumber(value),
            });
        }

        if (defaultCountry !== prevProps.defaultCountry) {
            this.setState({
                currentCountry: getCountryCode(
                    defaultCountry,
                    value ?? '',
                    this.state.phoneNumber,
                    this.state.currentCountry,
                    this.state.currentCallingCode,
                    this.state.isTouched,
                ),
            });
        }
    }

    public render() {
        return (
            <Field
                name={this.ID}
                validate={this.validatePhoneNumber}
                render={this.renderInputField}
            />
        );
    }

    private validatePhoneNumber(value: string): IErrorMessage {
        value = value.trim();
        const { required, validatorRules = [] } = this.props;
        const { currentCallingCode } = this.state;

        let errorMessage: IErrorMessage = '';
        errorMessage = validatePhoneNumber(
            value,
            this.state.initialDisplayValue,
            required || false,
            currentCallingCode,
        );

        if (errorMessage === '') {
            if (value && validatorRules.length > 0) {
                const err = this.phoneNumberFieldValidator.validate(value, this.LABEL_ID);
                if (err !== null) {
                    errorMessage = err.message;
                }
            }
        }

        return errorMessage;
    }

    private renderInputField({
        form: { touched, errors, handleBlur, handleChange, setFieldValue },
    }: FieldProps): ReactNode {
        const { autoFocus, callingCodeFieldId, value, required, tooltipText } = this.props;
        const { phoneNumber, currentCountry, initialDisplayValue, isTouched, maxLength } =
            this.state;
        const errorMessage =
            (isTouched || (touched && touched[this.ID])) && errors && errors[this.ID]
                ? errors[this.ID]
                : undefined;
        const onFocus = () => {
            if (!isTouched) {
                setFieldValue(this.ID, initialDisplayValue);
            }
            this.setState({
                isTouched: true,
            });
        };

        const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            if (phoneNumber) {
                setFieldValue(this.ID, phoneNumber.formatInternational());
            }
            handleBlur(event);
        };

        return (
            <PhoneNumberFieldWrapper data-qa-id="phone-number">
                <PhoneNumberFieldInputWrapper>
                    <FlagButtonWrapper>
                        <CallingCodeSelectorContainer
                            countryCode={currentCountry}
                            onCountryCodeSet={(country) => {
                                const callingCode = `+${country.dialCode}`;
                                if (
                                    !currentCountry ||
                                    country.alpha2Code.toLowerCase() !==
                                        currentCountry.toLowerCase()
                                ) {
                                    setFieldValue(this.ID, callingCode);
                                    if (callingCodeFieldId) {
                                        setFieldValue(callingCodeFieldId, callingCode);
                                    }
                                }

                                if (!isTouched && isEmpty(value)) {
                                    this.setState({
                                        initialDisplayValue: getDefaultValue(callingCode),
                                    });
                                }

                                this.setState({
                                    currentCallingCode: callingCode,
                                    currentCountry: country.alpha2Code,
                                });
                            }}
                            inputId={this.ID}
                        />
                    </FlagButtonWrapper>
                    <InputField
                        autoComplete="tel"
                        autoFocus={autoFocus}
                        id={this.ID}
                        labelMessage={TranslationHelpers.getMessageDescriptor(this.LABEL_ID)}
                        maxLength={maxLength}
                        value={isTouched && value !== '' ? value : initialDisplayValue}
                        required={required}
                        type="tel"
                        tabIndex={0}
                        onBlur={onBlur}
                        onPaste={(event) => {
                            const pastedNumber = event.clipboardData.getData('text/plain');
                            this.setState({
                                maxLength: Math.max(this.state.maxLength, pastedNumber.length),
                            });
                        }}
                        onChange={(event) => {
                            const curCallingCode = String(this.state.currentCallingCode).trim();
                            event.target.value = phoneNumberOnChange(
                                event,
                                curCallingCode,
                                this.state.currentCountry,
                            );
                            // when there's nothing in phone num field, remove the calling code flag value
                            if (event.target.value.trim() === '') {
                                this.setState({
                                    currentCallingCode: '',
                                });
                            }
                            handleChange(event);
                        }}
                        onFocus={onFocus}
                        error={errorMessage}
                    />
                    {tooltipText ? (
                        <InfoTooltip
                            id={this.ID}
                            tooltipText={TranslationHelpers.getMessageDescriptor(
                                'tooltip.phone_number',
                            )}
                            touchSize={[40, 40]}
                            uniqueId={this.ID}
                        />
                    ) : (
                        ''
                    )}
                </PhoneNumberFieldInputWrapper>
            </PhoneNumberFieldWrapper>
        );
    }
}

export default injectIntl(PhoneNumberField);
